/**
 * The announcement editor (phase 7b item 5), controlled: the view owns the
 * draft (`lib/announcementDraft.ts`) and judges it; this renders the form —
 * title, body, kind, severity, start and end, public, the audience (every
 * tenant, or chosen ones) and the status-page components it concerns — and
 * emits edits, save, revert, "end now" and delete.
 *
 * Public forces every tenant (a public announcement is the status page's,
 * and the contract refuses a targeted one), so the audience is locked while
 * it is on.
 */
import { type AnnouncementKindWire, type AnnouncementSeverityWire, announcementKindWireSchema, announcementSeverityWireSchema, STATUS_COMPONENTS, type StatusComponentWire } from '@norna/api-contract/vendor.js';
import type { AnnouncementDraft } from '../../lib/announcementDraft.js';
import { withPublic } from '../../lib/announcementDraft.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card.js';
import { Field, FieldDescription } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { Textarea } from '../ui/textarea.js';

const COMPONENT_LABEL: Readonly<Record<StatusComponentWire, string>> = { api: 'Cloud API', websocket: 'WebSocket', 'sign-in': 'Sign-in', 'cloud-productions': 'Cloud productions' };
const KIND_LABEL: Readonly<Record<AnnouncementKindWire, string>> = { announcement: 'Announcement', maintenance: 'Maintenance', incident: 'Incident' };
const SEVERITY_LABEL: Readonly<Record<AnnouncementSeverityWire, string>> = { info: 'Info', minor: 'Minor', major: 'Major', critical: 'Critical' };

export type AnnouncementEditorProps = {
  readonly mode: 'create' | 'edit';
  readonly draft: AnnouncementDraft;
  readonly tenants: readonly { readonly tenantId: string; readonly name: string }[];
  /** What stops a save (the draft judged against the contract), for the user. */
  readonly problems: readonly string[];
  /** The version being edited (edit mode), and who wrote it. */
  readonly version: number | null;
  readonly updatedBy: string | null;
  readonly dirty: boolean;
  readonly canSave: boolean;
  readonly pending: boolean;
  readonly onDraft: (draft: AnnouncementDraft) => void;
  readonly onSave: () => void;
  readonly onRevert: () => void;
  /** Set the end to now (ends an incident); edit mode. */
  readonly onEndNow: () => void;
  readonly onDelete: () => void;
};

function toggled<T>(list: readonly T[], value: T, on: boolean): T[] {
  return on ? [...list.filter((v) => v !== value), value] : list.filter((v) => v !== value);
}

type FieldsProps = { readonly draft: AnnouncementDraft; readonly locked: boolean; readonly onDraft: (draft: AnnouncementDraft) => void };

function KindAndTimes({ draft, locked, onDraft }: FieldsProps) {
  const set = (change: Partial<AnnouncementDraft>) => onDraft({ ...draft, ...change });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field>
        <Label htmlFor="announcement-kind">Kind</Label>
        <NativeSelect id="announcement-kind" value={draft.kind} onChange={(e) => set({ kind: announcementKindWireSchema.catch(draft.kind).parse(e.target.value) })} disabled={locked}>
          {announcementKindWireSchema.options.map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <Field>
        <Label htmlFor="announcement-severity">Severity</Label>
        <NativeSelect id="announcement-severity" value={draft.severity} onChange={(e) => set({ severity: announcementSeverityWireSchema.catch(draft.severity).parse(e.target.value) })} disabled={locked}>
          {announcementSeverityWireSchema.options.map((s) => (
            <option key={s} value={s}>
              {SEVERITY_LABEL[s]}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <Field>
        <Label htmlFor="announcement-starts">Starts</Label>
        <Input id="announcement-starts" type="datetime-local" value={draft.startsAt} onChange={(e) => set({ startsAt: e.target.value })} disabled={locked} />
      </Field>
      <Field>
        <Label htmlFor="announcement-ends">Ends</Label>
        <Input id="announcement-ends" type="datetime-local" value={draft.endsAt} onChange={(e) => set({ endsAt: e.target.value })} disabled={locked} />
        <FieldDescription>Empty: until ended.</FieldDescription>
      </Field>
    </div>
  );
}

function Audience({ draft, locked, onDraft, tenants }: FieldsProps & { readonly tenants: AnnouncementEditorProps['tenants'] }) {
  const set = (change: Partial<AnnouncementDraft>) => onDraft({ ...draft, ...change });
  const choosing = draft.audience === 'chosen' && !draft.public;
  return (
    <>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={draft.public} onChange={(e) => onDraft(withPublic(draft, e.target.checked))} disabled={locked} data-testid="announcement-public" />
        Public: also on the status page (every tenant)
      </label>
      <fieldset className="grid gap-2" disabled={locked || draft.public}>
        <legend className="text-sm font-medium">Audience</legend>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="announcement-audience" checked={draft.audience === 'all'} onChange={() => set({ audience: 'all' })} data-testid="audience-all" />
          Every tenant
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="announcement-audience" checked={draft.audience === 'chosen'} onChange={() => set({ audience: 'chosen' })} data-testid="audience-chosen" />
          Chosen tenants
        </label>
        {choosing ? (
          <div className="grid gap-1 pl-6" data-testid="audience-tenants">
            {tenants.map((t) => (
              <label key={t.tenantId} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.tenantIds.includes(t.tenantId)} onChange={(e) => set({ tenantIds: toggled(draft.tenantIds, t.tenantId, e.target.checked) })} />
                {t.name} <span className="font-mono text-xs text-muted-foreground">{t.tenantId}</span>
              </label>
            ))}
          </div>
        ) : null}
      </fieldset>
    </>
  );
}

function Components({ draft, locked, onDraft }: FieldsProps) {
  return (
    <fieldset className="grid gap-2" disabled={locked}>
      <legend className="text-sm font-medium">Status-page components it concerns</legend>
      <div className="flex flex-wrap gap-4">
        {STATUS_COMPONENTS.map((c) => (
          <label key={c} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.components.includes(c)} onChange={(e) => onDraft({ ...draft, components: toggled(draft.components, c, e.target.checked) })} />
            {COMPONENT_LABEL[c]}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Actions({ mode, dirty, problems, pending, onSave, onRevert, onEndNow, onDelete }: Pick<AnnouncementEditorProps, 'mode' | 'dirty' | 'problems' | 'pending' | 'onSave' | 'onRevert' | 'onEndNow' | 'onDelete'>) {
  const editing = mode === 'edit';
  const saveBlocked = (editing && !dirty) || problems.length > 0 || pending;
  const saveLabel = editing ? 'Save' : 'Publish';
  return (
    <CardFooter className="flex flex-wrap gap-2">
      {editing ? (
        <>
          <Button variant="outline" onClick={onDelete} disabled={pending} data-testid="delete-announcement">
            Delete
          </Button>
          <Button variant="outline" onClick={onEndNow} disabled={pending} data-testid="end-announcement">
            End now
          </Button>
        </>
      ) : null}
      <Button variant="outline" className="ml-auto" onClick={onRevert} disabled={!dirty || pending}>
        {editing ? 'Revert' : 'Clear'}
      </Button>
      <Button onClick={onSave} disabled={saveBlocked} data-testid="save-announcement">
        {pending ? 'Saving…' : saveLabel}
      </Button>
    </CardFooter>
  );
}

function describe(props: Pick<AnnouncementEditorProps, 'mode' | 'version' | 'updatedBy' | 'dirty'>): string {
  const base = props.mode === 'edit' && props.version !== null ? `Version ${String(props.version)} · last written by ${props.updatedBy ?? '?'}` : 'Shown in-app to the tenants it targets while in force.';
  return props.dirty ? `${base} · unsaved changes` : base;
}

export function AnnouncementEditor(props: AnnouncementEditorProps) {
  const { mode, draft, tenants, problems, dirty, canSave, pending, onDraft } = props;
  const locked = !canSave || pending;
  const title = mode === 'create' ? 'New announcement' : draft.title === '' ? 'Announcement' : draft.title;
  return (
    <Card data-testid="announcement-editor">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{describe(props)}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Field>
          <Label htmlFor="announcement-title">Title</Label>
          <Input id="announcement-title" value={draft.title} onChange={(e) => onDraft({ ...draft, title: e.target.value })} disabled={locked} maxLength={200} />
        </Field>
        <Field>
          <Label htmlFor="announcement-body">Body</Label>
          <Textarea id="announcement-body" value={draft.body} onChange={(e) => onDraft({ ...draft, body: e.target.value })} disabled={locked} maxLength={5000} rows={4} />
        </Field>
        <KindAndTimes draft={draft} locked={locked} onDraft={onDraft} />
        <Audience draft={draft} locked={locked} onDraft={onDraft} tenants={tenants} />
        <Components draft={draft} locked={locked} onDraft={onDraft} />
        {problems.length > 0 && dirty ? (
          <ul className="grid gap-1 text-sm text-destructive" role="alert" data-testid="announcement-problems">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
      </CardContent>
      {canSave ? <Actions {...props} /> : null}
    </Card>
  );
}
